// Tenantra Inc. - Landlord View Report Page
// Uses the SAME flow as Tenant Portal "View My Report" + Analytics Dashboard
import { useState } from 'react';
import { FileText, Search, ArrowRight, CheckCircle, AlertCircle, Loader2, ArrowLeft, User, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function DobDropdowns({ value, onChange }) {
  const initParts = value ? value.split('-') : ['', '', ''];
  const [selYear, setSelYear] = useState(initParts[0] || '');
  const [selMonth, setSelMonth] = useState(initParts[1] ? String(parseInt(initParts[1])) : '');
  const [selDay, setSelDay] = useState(initParts[2] ? String(parseInt(initParts[2])) : '');

  const doUpdate = (y, m, d) => {
    if (y && m && d) {
      onChange(`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`);
    }
  };

  const handleMonth = (v) => { setSelMonth(v); doUpdate(selYear, v, selDay); };
  const handleDay = (v) => { setSelDay(v); doUpdate(selYear, selMonth, v); };
  const handleYear = (v) => { setSelYear(v); doUpdate(v, selMonth, selDay); };

  const years = [];
  for (let i = new Date().getFullYear() - 16; i >= 1920; i--) years.push(i);
  const maxDay = selMonth && selYear ? new Date(parseInt(selYear), parseInt(selMonth), 0).getDate() : 31;
  const days = [];
  for (let i = 1; i <= maxDay; i++) days.push(i);

  const sel = "h-10 px-2 text-sm bg-white border border-slate-300 rounded-md text-slate-900 focus:outline-none focus:ring-1 focus:ring-teal-600 appearance-none";

  return (
    <div className="flex gap-1.5">
      <div className="relative flex-1">
        <select value={selMonth} onChange={(e) => handleMonth(e.target.value)} className={`w-full ${sel}`} data-testid="report-dob-month">
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={i} value={String(i+1)}>{m}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[70px]">
        <select value={selDay} onChange={(e) => handleDay(e.target.value)} className={`w-full ${sel}`} data-testid="report-dob-day">
          <option value="">Day</option>
          {days.map(d => <option key={d} value={String(d)}>{d}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[85px]">
        <select value={selYear} onChange={(e) => handleYear(e.target.value)} className={`w-full ${sel}`} data-testid="report-dob-year">
          <option value="">Year</option>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
    </div>
  );
}

// Score category configurations
const SCORE_CATEGORIES = {
  excellent: { label: 'Excellent', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  good: { label: 'Good', color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' },
  fair: { label: 'Fair', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  poor: { label: 'Poor', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  insufficient_data: { label: 'New', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' }
};

// Full Consent Terms - RBC Style Scrollable
const CONSENT_TERMS = `TENANT SCREENING REPORT ACCESS AGREEMENT

By accessing this Tenant Screening Report through Tenantra Inc., you ("Landlord") acknowledge and agree to the following terms and conditions:

1. PURPOSE OF ACCESS
You confirm that you are accessing this report for the sole purpose of evaluating a prospective tenant for a legitimate rental housing transaction. You represent that you have received consent from the tenant to access their rental history report.

2. COMPLIANCE WITH PIPEDA
You acknowledge that the Personal Information Protection and Electronic Documents Act (PIPEDA) governs the collection, use, and disclosure of personal information in the course of commercial activities in Canada. You agree to comply with all applicable provisions of PIPEDA in your use of this report.

3. PERMITTED USE
The information contained in this report may only be used for:
- Evaluating the tenant's suitability for a rental property you own or manage
- Making informed decisions regarding rental applications
- Verifying information provided by the tenant

4. PROHIBITED USE
You agree NOT to:
- Share, sell, or distribute this report to any third party
- Use the information for any discriminatory purposes prohibited by human rights legislation
- Store or retain the report beyond the duration necessary for the rental decision
- Use the information for any purpose other than tenant screening

5. ACCURACY AND LIMITATIONS
Tenantra Inc. provides this report based on information submitted by previous landlords. While we strive for accuracy, we do not guarantee the completeness or accuracy of all information. You should use this report as one of several factors in your tenant evaluation process.

6. CONFIDENTIALITY
You agree to maintain the confidentiality of all personal information contained in this report. You will implement appropriate security measures to prevent unauthorized access, disclosure, or use of this information.

7. INDEMNIFICATION
You agree to indemnify and hold harmless Tenantra Inc. from any claims, damages, or expenses arising from your misuse of this report or violation of these terms.

8. RECORD OF ACCESS
You acknowledge that Tenantra Inc. maintains records of all report access for audit and compliance purposes. The tenant may be notified that their report was accessed.

9. GOVERNING LAW
This agreement is governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein.

By checking the box below and proceeding, you confirm that you have read, understood, and agree to abide by these terms and conditions.

Last Updated: December 2024
Tenantra Inc. - Canada's Rental History Platform`;


export default function LandlordViewReportPage() {
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    tenant_unique_id: ''
  });
  const [step, setStep] = useState('lookup'); // lookup, loading, consent, report
  const [lookupMode, setLookupMode] = useState(null); // 'id' or 'name'
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Searching tenant records...');

  const handleLookup = async (e) => {
    e.preventDefault();
    setError('');
    setLoadingMessage('Searching tenant records...');
    setStep('loading');
    
    try {
      const payload = lookupMode === 'id'
        ? { tenant_unique_id: formData.tenant_unique_id, date_of_birth: formData.date_of_birth }
        : { full_name: formData.full_name, date_of_birth: formData.date_of_birth };
      const [response] = await Promise.all([
        api.post('/tenant-portal/landlord-screening', payload),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);
      setReportData(response.data);
      setStep('consent');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No tenant found with this information. Please check and try again.');
      } else {
        setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      }
      setStep('lookup');
    }
  };

  const handleViewReport = () => {
    if (!consentAccepted) {
      toast.error('Please accept the screening agreement to continue');
      return;
    }
    setLoadingMessage('Preparing report...');
    setStep('loading');
    setTimeout(() => {
      setStep('report');
    }, 1200);
  };

  const handleStartOver = () => {
    setStep('lookup');
    setLookupMode(null);
    setFormData({ full_name: '', date_of_birth: '', tenant_unique_id: '' });
    setReportData(null);
    setConsentAccepted(false);
    setHasScrolledToBottom(false);
    setError('');
    setLoadingMessage('Searching tenant records...');
  };

  const scoreConfig = reportData?.score ? SCORE_CATEGORIES[reportData.score.category] || SCORE_CATEGORIES.insufficient_data : null;

  // Full-screen Loading State with Spinner
  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center py-32" data-testid="screening-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-sm">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // Score View - quick check only; full history lives in the tenant's own Rent Report
  if (step === 'report' && reportData) {
    const sc = reportData.score || {};
    const value = sc.score;
    const BANDS = [
      { key: 'poor', label: 'Poor', min: 0, max: 54, text: 'Repeated late or missed rent, lease breaches or property issues on record.' },
      { key: 'fair', label: 'Fair', min: 55, max: 69, text: 'Mixed record. Some late payments or issues reported by previous landlords.' },
      { key: 'good', label: 'Good', min: 70, max: 84, text: 'Reliable record with minor exceptions. Typical of a dependable tenant.' },
      { key: 'excellent', label: 'Excellent', min: 85, max: 100, text: 'Consistently on-time rent, lease fulfilled, no property issues reported.' },
    ];
    const band = BANDS.find(b => b.key === sc.category);
    const factors = [
      { label: 'Payment history', d: sc.payment_history },
      { label: 'Lease compliance', d: sc.lease_compliance },
      { label: 'Property care', d: sc.property_care },
    ];

    return (
      <div className="max-w-2xl mx-auto space-y-4" data-testid="screening-score-view">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900" data-testid="screening-report-title">Tenant Score</h1>
            <p className="text-sm text-slate-500">{reportData.tenant_name}{reportData.tenant_unique_id ? ` · ID ${reportData.tenant_unique_id}` : ''}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleStartOver} className="h-8 text-xs" data-testid="screening-new-search-btn">
            <Search className="w-3 h-3 mr-1.5" />New Search
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-bold text-slate-900 tabular-nums" data-testid="screening-score-value">{value ?? '—'}</span>
                <span className="text-base text-slate-400">/ 100</span>
              </div>
              <p className={`text-sm font-semibold mt-1 ${scoreConfig?.color || 'text-slate-500'}`} data-testid="screening-score-band">
                {value === null || value === undefined ? 'Not enough data' : scoreConfig?.label}
              </p>
            </div>
            <p className="text-xs text-slate-500 text-right">
              Based on {sc.total_reports ?? 0} landlord report{sc.total_reports === 1 ? '' : 's'}
              {sc.calculated_at ? <><br />Calculated {new Date(sc.calculated_at).toLocaleDateString('en-CA')}</> : null}
            </p>
          </div>

          <div className="relative h-2 rounded-full overflow-hidden flex mb-1.5">
            <div className="bg-red-300" style={{ width: '55%' }} />
            <div className="bg-amber-300" style={{ width: '15%' }} />
            <div className="bg-teal-300" style={{ width: '15%' }} />
            <div className="bg-emerald-400" style={{ width: '15%' }} />
            {value !== null && value !== undefined && (
              <div className="absolute top-0 bottom-0 w-0.5 bg-slate-900" style={{ left: `calc(${value}% - 1px)` }} />
            )}
          </div>
          <div className="grid grid-cols-4 text-[11px] text-slate-500 mb-5">
            {BANDS.map(b => (
              <span key={b.key} className={`${band?.key === b.key ? 'text-slate-900 font-semibold' : ''} ${b.key === 'poor' ? '' : 'text-center'} ${b.key === 'excellent' ? 'text-right' : ''}`}>
                {b.label} {b.min}–{b.max}
              </span>
            ))}
          </div>

          <p className="text-sm text-slate-700 leading-relaxed" data-testid="screening-score-meaning">
            {band ? band.text : 'Fewer than three landlord reports exist for this tenant, so a score cannot be calculated yet.'}
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {factors.map(f => (
            <div key={f.label} className="flex items-center gap-4 px-5 py-3">
              <span className="text-sm text-slate-700 w-40">{f.label}</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-700 rounded-full" style={{ width: `${f.d?.percentage ?? 0}%` }} />
              </div>
              <span className="text-sm text-slate-900 tabular-nums w-16 text-right">{f.d?.score ?? 0} / {f.d?.max_score ?? 0}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-500">
          This score is a screening indicator only. The full rental history belongs to the tenant and is available to them through their Rent Report.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Progress Steps */}
      <div className="flex items-center gap-1.5 mb-5" data-testid="screening-step-track">
        {['Find tenant', 'Agreement', 'Report'].map((label, i) => {
          const n = i + 1;
          const idx = step === 'consent' ? 2 : 1;
          const reached = idx >= n;
          const done = idx > n;
          return (
            <div key={label} className="flex items-center gap-1.5 flex-1 last:flex-none">
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${
                  done ? 'bg-emerald-600 text-white' : reached ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-400'
                }`}>{done ? <CheckCircle className="w-3.5 h-3.5" /> : n}</div>
                <span className={`hidden sm:block text-[11px] font-medium whitespace-nowrap ${idx === n ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
              </div>
              {n < 3 && <div className={`flex-1 h-0.5 rounded-full ${idx > n ? 'bg-teal-600' : 'bg-slate-200'}`} />}
            </div>
          );
        })}
      </div>

      {/* Form Card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        
        {step === 'lookup' && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                {!lookupMode ? 'Screen a tenant' : lookupMode === 'id' ? 'Tenant ID' : 'Tenant name'}
              </h2>
            </div>

            {/* Mode Selection - Same as Tenant Portal */}
            {!lookupMode ? (
              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => setLookupMode('id')}
                  className="w-full p-3.5 rounded-md border border-slate-300 hover:border-teal-600 hover:bg-teal-50/40 transition-colors text-left group"
                  data-testid="screen-mode-id"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-teal-700 flex-shrink-0" />
                    <p className="flex-1 text-slate-900 font-medium text-sm">I have their Tenant ID</p>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-700 transition-colors" />
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setLookupMode('name')}
                  className="w-full p-3.5 rounded-md border border-slate-300 hover:border-teal-600 hover:bg-teal-50/40 transition-colors text-left group"
                  data-testid="screen-mode-name"
                >
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-teal-700 flex-shrink-0" />
                    <p className="flex-1 text-slate-900 font-medium text-sm">I don't have their Tenant ID</p>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-700 transition-colors" />
                  </div>
                </button>

                <p className="pt-3 text-xs text-slate-500">
                  Tenant consent is required. Searches are logged.
                </p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setLookupMode(null); setError(''); }}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4"
                  data-testid="screen-back-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back</span>
                </button>

                <form onSubmit={handleLookup} className="space-y-4">
                  {lookupMode === 'id' ? (
                    <div className="space-y-1.5">
                      <Label className="text-sm text-slate-700">Tenant ID</Label>
                      <Input
                        inputMode="numeric"
                        maxLength={6}
                        value={formData.tenant_unique_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, tenant_unique_id: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        className="h-10 bg-white border-slate-300 font-mono tracking-widest focus:border-teal-600 focus:ring-teal-600/20"
                        required
                        data-testid="screen-tenant-id-input"
                      />
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-sm text-slate-700">Full legal name</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                        className="h-10 bg-white border-slate-300 focus:border-teal-600 focus:ring-teal-600/20"
                        required
                        data-testid="screen-tenant-name-input"
                      />
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-sm text-slate-700">Date of birth</Label>
                    <DobDropdowns
                      value={formData.date_of_birth}
                      onChange={(v) => setFormData(prev => ({ ...prev, date_of_birth: v }))}
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-700">{error}</p>
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-sm" data-testid="screen-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue<ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </form>
              </>
            )}
          </>
        )}

        {step === 'consent' && reportData && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Screening agreement</h2>
            </div>

            <div className="p-3 border border-slate-200 rounded-md mb-4 flex items-center justify-between" data-testid="screen-found-tenant">
              <div>
                <p className="text-sm font-medium text-slate-900">{reportData.tenant_name}</p>
                <p className="text-xs text-slate-500 font-mono">Tenant ID {reportData.tenant_unique_id || '—'}</p>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle className="w-3.5 h-3.5" />Record found</span>
            </div>

            {/* Scrollable Consent Agreement - RBC Style */}
            <div className="mb-4">
              <div 
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;
                  if (scrollTop + clientHeight >= scrollHeight - 10) {
                    setHasScrolledToBottom(true);
                  }
                }}
                className="h-48 overflow-y-auto border border-slate-300 rounded-md p-4 bg-slate-50 text-xs text-slate-700 leading-relaxed"
                data-testid="screen-agreement-scroll"
              >
                <pre className="whitespace-pre-wrap font-sans">{CONSENT_TERMS}</pre>
              </div>
              {!hasScrolledToBottom && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Scroll to the end of the agreement to continue
                </p>
              )}
            </div>

            <label className={`flex items-start gap-2.5 mb-4 cursor-pointer p-3 rounded-lg border transition-all ${
              hasScrolledToBottom 
                ? 'bg-white border-slate-200 hover:border-teal-300' 
                : 'bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed'
            }`}>
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => hasScrolledToBottom && setConsentAccepted(e.target.checked)}
                disabled={!hasScrolledToBottom}
                data-testid="screen-consent-checkbox"
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
              />
              <span className={`text-sm ${hasScrolledToBottom ? 'text-slate-700' : 'text-slate-500'}`}>
                I have read and agree to the Tenant Screening Report Access Agreement
              </span>
            </label>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStep('lookup'); setHasScrolledToBottom(false); setConsentAccepted(false); }} className="flex-1 h-10">
                Back
              </Button>
              <Button onClick={handleViewReport} disabled={!consentAccepted} className="flex-1 h-10 bg-teal-600 hover:bg-teal-700 disabled:opacity-50" data-testid="screen-view-report-btn">
                View Report
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
